import get from 'lodash/get'
import PCLNAnalytics from '@pcln/analytics'
import { storageConstants } from '../util'
import { removeExpiredCache, writeToStorage } from '../util/storageUtil'

export const getIsAllDelta = airlines =>
  airlines.every(airline => airline.code === 'DL' || airline.code === '3+')

export const buildPriceRequest = ({
  token,
  searchParams,
  alreadyPriceConfirmed,
  selectedFares,
  isMobile,
  numberOfFareBrands,
  gordianCarriers,
  fromPackages,
  displayGordianSeats,
  displayGordianForPackages,
  SETI,
  airlines,
}) => {
  const adults = Number(get(searchParams, '[num-adults]', 0))
  const youth = Number(get(searchParams, '[num-youths]', 0))
  const children = Number(get(searchParams, '[num-children]', 0))
  const infants = Number(get(searchParams, '[num-infants]', 0))
  const airPriceMetaInfo = {
    appCode: isMobile ? 'MOBILEWEB' : 'DESKTOP',
    alreadyPriceConfirmed,
    selectedFares,
    adults: !isNaN(adults) ? adults : 1,
    youths: !isNaN(youth) ? youth : 0,
    children: !isNaN(children) ? children : 0,
    infants: !isNaN(infants) ? infants : 0,
    gordianCarriers,
    displayGordianSeats,
    displayGordianForPackages,
    numberOfFareBrands,
    SETI,
    isOnlyDisplayingCoreAncillaries: getIsAllDelta(airlines),
  }
  if (fromPackages) {
    airPriceMetaInfo.pkgRequest = getPkgRequest(searchParams)
  }
  return {
    token,
    includeRentalCar: false,
    airPriceMetaInfo,
    isPackages: fromPackages,
  }
}

export const getPkgRequest = searchParams => {
  return {
    cartId: get(searchParams, 'cart-key'),
    searchQuery: {
      componentKey: get(searchParams, 'component-key'),
    },
  }
}

export const retrieveGraphData = ({
  graphResponse,
  alreadyPriceConfirmed,
  priceFromItineraryLookUp,
  fromPackages,
  cabinClass,
}) => {
  const {
    pricingInfo,
    aaSpecificInfo,
    itineraryReference,
    passportRequired,
    nationalityRequired,
    isSeatEligible,
    fareBrandsInfo,
    baggageFeeDataByFareBrand,
  } = graphResponse
  const transformedData = {
    priceConfirmationId: itineraryReference?.pricedConfirmationId,
    passportRequired,
    nationalityRequired,
    groudId: itineraryReference?.groupId,
    refId: itineraryReference?.refId,
  }
  if (aaSpecificInfo?.hasAmerican) {
    const bookingClass = aaSpecificInfo?.sliceInfo?.bookingClass
    const aaCabinClass = cabinClass[aaSpecificInfo.sliceInfo.sliceId].code
    if (!alreadyPriceConfirmed && aaCabinClass === 'BEC') {
      transformedData.basicBkgClass = bookingClass
    } else if (aaCabinClass !== 'BEC') {
      transformedData.upgradeBkgClass = bookingClass
    }
  }
  transformedData.hasSeatChoiceOption = isSeatEligible
  const priceFromGraph = pricingInfo.amount
  // Affirm: Update total cost after PC returned
  transformedData.affirmAllPassengerCost = Math.ceil(
    parseFloat(pricingInfo.totalTripPrice) * 100
  )
  if (pricingInfo?.priceChanged) {
    const previousTotalFare =
      parseFloat(priceFromItineraryLookUp.dollar) +
      parseFloat(`.${priceFromItineraryLookUp.cents}`)
    const priceChangeAmount = getPriceChangeAmount(
      priceFromGraph,
      previousTotalFare
    )
    transformedData.dollar = pricingInfo.dollar
    transformedData.cents = pricingInfo.cents
    transformedData.priceChangeAmount = priceChangeAmount
  }
  if (fareBrandsInfo?.fareBrandsByKey) {
    transformedData.fareBrandsInfo = fareBrandsInfo
    const fareBrands = fareBrandsInfo?.fareBrandsByKey
    const fareBrandsByMap = {}
    fareBrands.forEach(eachFareBrand => {
      // const allowableAncillaries = eachFareBrand.coreAncillaries.forEach((eachAncillary) => {
      //   return allowableAncillaryNames.includes(eachAncillary.name);
      // });
      fareBrandsByMap[eachFareBrand?.key] = eachFareBrand
    })
    transformedData.hasUpsellOptions = !!get(
      fareBrandsInfo,
      'upsellOptions.length',
      0
    )
    fareBrandsInfo.fareBrandsByKeyMap = fareBrandsByMap
  }
  if (fromPackages && baggageFeeDataByFareBrand) {
    const baggageFeeDataByFareBrandMap = {}
    baggageFeeDataByFareBrand.forEach(eachFB => {
      if (eachFB?.fareBrandKey) {
        baggageFeeDataByFareBrandMap[eachFB.fareBrandKey] = eachFB
      }
    })
    transformedData.baggageFeeDataByFareBrand = baggageFeeDataByFareBrandMap
  }
  return transformedData
}

export const getPriceChangeAmount = (currentTotalFare, previousTotalFare) => {
  const changeAmount = currentTotalFare - previousTotalFare
  if (changeAmount < 1 && changeAmount > -1) {
    return Number(changeAmount.toFixed(2))
  }
  return Math.ceil(changeAmount)
}

export const updatePriceInStorage = (pricingInfo, flightString) => {
  try {
    const storageKey =
      `${storageConstants.AIRCACHE_PREFIX}-${storageConstants.FLIGHT_PRICE_CACHE}` +
      `-${flightString}`
    const lastVisitedStorage = JSON.parse(localStorage.getItem(storageKey))
    const lastVisitedPrice =
      lastVisitedStorage && lastVisitedStorage.price
        ? lastVisitedStorage.price
        : `${pricingInfo.dollar}.${pricingInfo.cents}`
    const value = {
      price: `${pricingInfo.dollar}.${pricingInfo.cents}`,
      deleteAfter: flightString.split('*')[1],
    }
    const tag = {
      action: 'FLIGHT-PRICE-DETAIL',
      label: 'FLIGHT-STRING',
      value: storageKey,
      price: value.price,
    }
    PCLNAnalytics.log(tag)
    writeToStorage(
      storageConstants.LOCAL_TYPE,
      storageKey,
      JSON.stringify(value)
    )
    window.addEventListener('unload', () => {
      removeExpiredCache()
    })
    if (lastVisitedPrice && !isNaN(lastVisitedPrice)) {
      return `${pricingInfo.dollar}.${pricingInfo.cents}` - lastVisitedPrice
    }
    return 0
  } catch (error) {
    const tag = {
      action: 'FLIGHT-PRICE-Storage',
      label: 'Error',
      value: 'JSON.stringify(error)',
    }
    PCLNAnalytics.log(tag)
    return 0
  }
}
